import { Navigate, NavLink, Outlet, useNavigate } from "react-router-dom";
import {
  Crown, LayoutDashboard, FileText, Images, Sparkles, Quote, Newspaper, Inbox, ClipboardList, LogOut, ExternalLink,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const NAV = [
  { to: "/admin", end: true, label: "Dashboard", icon: LayoutDashboard, testid: "admin-nav-dashboard" },
  { to: "/admin/content", label: "Site Content", icon: FileText, testid: "admin-nav-content" },
  { to: "/admin/gallery", label: "Gallery", icon: Images, testid: "admin-nav-gallery" },
  { to: "/admin/services", label: "Pageants", icon: Sparkles, testid: "admin-nav-services" },
  { to: "/admin/testimonials", label: "Testimonials", icon: Quote, testid: "admin-nav-testimonials" },
  { to: "/admin/blog", label: "Blog & News", icon: Newspaper, testid: "admin-nav-blog" },
  { to: "/admin/messages", label: "Messages", icon: Inbox, testid: "admin-nav-messages" },
  { to: "/admin/registrations", label: "Registrations", icon: ClipboardList, testid: "admin-nav-registrations" },
];

export default function AdminLayout() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();

  if (user === null) {
    return (
      <div data-testid="admin-loading" className="flex h-screen items-center justify-center bg-[#050505]">
        <Crown className="animate-crown-pulse h-10 w-10 text-[#D4AF37]" strokeWidth={1} />
      </div>
    );
  }
  if (user === false) return <Navigate to="/admin/login" replace />;

  const handleLogout = async () => {
    await logout();
    navigate("/admin/login", { replace: true });
  };

  const linkCls = ({ isActive }) =>
    `flex items-center gap-3 px-4 py-3 text-sm tracking-wide transition-all duration-300 ${
      isActive
        ? "border-l-2 border-[#D4AF37] bg-[#D4AF37]/10 text-[#D4AF37]"
        : "border-l-2 border-transparent text-neutral-400 hover:bg-white/5 hover:text-white"
    }`;

  return (
    <div data-testid="admin-layout" className="flex min-h-screen bg-[#050505] text-white">
      <aside className="fixed inset-y-0 left-0 z-40 hidden w-64 flex-col border-r border-white/10 bg-black/60 backdrop-blur-xl lg:flex">
        <div className="flex items-center gap-3 border-b border-white/10 px-6 py-6">
          <Crown className="h-6 w-6 text-[#D4AF37]" strokeWidth={1} />
          <div>
            <div className="font-script text-2xl leading-none text-[#D4AF37]">D'La Valentina</div>
            <div className="mt-1 text-[9px] uppercase tracking-[0.3em] text-neutral-500">Admin Court</div>
          </div>
        </div>
        <nav className="flex-1 space-y-1 overflow-y-auto p-3">
          {NAV.map((n) => (
            <NavLink key={n.to} to={n.to} end={n.end} data-testid={n.testid} className={linkCls}>
              <n.icon className="h-4 w-4" strokeWidth={1.5} />
              {n.label}
            </NavLink>
          ))}
        </nav>
        <div className="border-t border-white/10 p-3">
          <a href="/" target="_blank" rel="noopener noreferrer" data-testid="admin-view-site-link" className="flex items-center gap-3 px-4 py-3 text-sm text-neutral-400 transition-colors hover:text-[#D4AF37]">
            <ExternalLink className="h-4 w-4" strokeWidth={1.5} /> View Website
          </a>
          <button data-testid="admin-logout-button" onClick={handleLogout} className="flex w-full items-center gap-3 px-4 py-3 text-sm text-neutral-400 transition-colors hover:text-red-400">
            <LogOut className="h-4 w-4" strokeWidth={1.5} /> Logout
          </button>
        </div>
      </aside>

      <div className="flex-1 lg:pl-64">
        <div className="sticky top-0 z-30 flex items-center gap-2 overflow-x-auto border-b border-white/10 bg-black/80 px-4 py-3 backdrop-blur-xl lg:hidden">
          {NAV.map((n) => (
            <NavLink key={n.to} to={n.to} end={n.end} data-testid={`admin-mobile-${n.label.toLowerCase().replace(/\s|&/g, "-")}`} className={linkCls}>
              <n.icon className="h-4 w-4" strokeWidth={1.5} />
              <span className="whitespace-nowrap text-xs">{n.label}</span>
            </NavLink>
          ))}
          <button data-testid="admin-mobile-logout" onClick={handleLogout} className="px-3 text-neutral-400 hover:text-red-400">
            <LogOut className="h-4 w-4" strokeWidth={1.5} />
          </button>
        </div>
        <main className="p-6 md:p-10">
          <Outlet />
        </main>
      </div>
    </div>
  );
}
